import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Lock, KeyRound, SearchX, Copy, HelpCircle, ArrowRight, MapPin, Clock, ShieldCheck, CreditCard } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1707296916219-4f2d7529a4c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxhdXRvbW90aXZlJTIwbG9ja3NtaXRofGVufDB8fHx8MTc3Nzg2MTQ5M3ww&ixlib=rb-4.1.0&q=85";
const KEYS_IMG = "https://images.unsplash.com/photo-1761264889404-a194af20ae90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjYXIlMjBrZXklMjBmb2J8ZW58MHx8fHwxNzc3ODYxNDkzfDA&ixlib=rb-4.1.0&q=85";
const BMW_IMG = "https://images.unsplash.com/photo-1555215695-3004980ad54e?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";

const services = [
  { icon: Lock, title: "Llaves dentro", desc: "Apertura sin dañar. Incluso con niños o mascotas dentro.", price: "Desde 60€" },
  { icon: KeyRound, title: "Llave no funciona", desc: "Diagnóstico electrónico, reparación de mando o chip.", price: "Desde 120€" },
  { icon: SearchX, title: "Pérdida de llave", desc: "Codificación in situ. Nueva llave con mando programada.", price: "Desde 180€" },
  { icon: Copy, title: "Copia de llave", desc: "Duplicado con chip, telemandos y llaves de proximidad.", price: "Desde 80€" },
  { icon: HelpCircle, title: "Otro problema", desc: "Pagas solo el desplazamiento. El técnico evalúa y presupuesta in situ.", price: "Solo desplazamiento" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO} alt="Cerrajero de automoción" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
          <div className="absolute inset-0 grid-overlay opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-32">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#FFC107]/30 bg-[#FFC107]/5 rounded-full text-xs font-mono text-[#FFC107] mb-6 fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFC107] blink-dot" />
            SERVICIO ACTIVO · 24/7
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight fade-up max-w-4xl">
            Cerrajero de<br />
            <span className="text-amber-grad">automoción</span><br />
            a domicilio.
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#A1A1AA] max-w-xl fade-up leading-relaxed">
            Reserva online con precio cerrado. Llevamos tu coche de emergencia a rodar en menos de 45 minutos. Paga un 30% y el resto al finalizar.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 fade-up">
            <Link
              to="/reservar"
              className="btn-amber px-6 py-4 rounded-md font-semibold inline-flex items-center justify-center gap-2 text-sm"
              data-testid="hero-book-btn"
            >
              Reservar ahora <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#servicios"
              className="btn-outline px-6 py-4 rounded-md font-semibold inline-flex items-center justify-center gap-2 text-sm"
              data-testid="hero-services-btn"
            >
              Ver servicios
            </a>
          </div>

          {/* Trust strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden max-w-3xl">
            {[
              { icon: Clock, label: "Tiempo medio", value: "<45 min" },
              { icon: MapPin, label: "Cobertura", value: "100 km" },
              { icon: ShieldCheck, label: "Garantía", value: "12 meses" },
              { icon: CreditCard, label: "Pago online", value: "Seguro 100%" },
            ].map((s) => (
              <div key={s.label} className="bg-[#0A0A0A] p-4">
                <s.icon className="w-4 h-4 text-[#FFC107] mb-2" />
                <div className="text-xs text-[#71717A] uppercase tracking-wider font-mono">{s.label}</div>
                <div className="font-display font-semibold text-lg mt-0.5">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="servicios" className="max-w-7xl mx-auto px-5 md:px-8 py-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FFC107] mb-3">01 · Servicios</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight max-w-2xl">
              Cada emergencia, una solución clara.
            </h2>
          </div>
          <p className="text-sm text-[#A1A1AA] max-w-sm">
            Precios transparentes calculados según marca, año y zona. Sin sorpresas.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((s) => (
            <div key={s.title} className="sc-card rounded-md p-6 group" data-testid={`service-card-${s.title}`}>
              <div className="w-11 h-11 rounded-md bg-[#FFC107]/10 border border-[#FFC107]/20 flex items-center justify-center mb-5 group-hover:bg-[#FFC107] transition-colors duration-200">
                <s.icon className="w-5 h-5 text-[#FFC107] group-hover:text-black transition-colors" strokeWidth={2} />
              </div>
              <h3 className="font-display font-semibold text-xl mb-2 tracking-tight">{s.title}</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">{s.desc}</p>
              <div className="flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-xs font-mono uppercase tracking-wider text-[#71717A]">{s.price}</span>
                <ArrowRight className="w-4 h-4 text-[#FFC107] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="border-y border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 grid lg:grid-cols-2 gap-14">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FFC107] mb-3">02 · Cómo funciona</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">
              Reserva en 4 pasos, paga a tu ritmo.
            </h2>
            <p className="mt-5 text-[#A1A1AA] text-base max-w-md leading-relaxed">
              Cuéntanos qué te pasa, pon tu ubicación y recibe el precio cerrado al instante. Reserva con 30% y abona el resto cuando el cerrajero termine el trabajo.
            </p>
            <Link
              to="/reservar"
              className="btn-amber mt-8 px-6 py-4 rounded-md font-semibold inline-flex items-center gap-2 text-sm"
              data-testid="how-book-btn"
            >
              Empezar reserva <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {[
              { n: "01", t: "Describe la avería", d: "Llaves dentro, pérdida, fallo del mando o copia." },
              { n: "02", t: "Datos del vehículo", d: "Marca, modelo y año para la cotización precisa." },
              { n: "03", t: "Ubicación exacta", d: "GPS automático + dirección manual. Llegamos rápido." },
              { n: "04", t: "Precio cerrado y pago", d: "30% de depósito o pago completo. Tarjeta segura." },
            ].map((step) => (
              <div key={step.n} className="sc-card rounded-md p-5 flex gap-5 items-start">
                <span className="font-display font-bold text-3xl text-[#FFC107]/20">{step.n}</span>
                <div>
                  <h3 className="font-display font-semibold text-lg mb-1">{step.t}</h3>
                  <p className="text-sm text-[#A1A1AA]">{step.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE BAND */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 grid md:grid-cols-2 gap-4">
        <div className="relative overflow-hidden rounded-md border border-white/10 aspect-[4/3]">
          <img src={KEYS_IMG} alt="Llaves de coche modernas" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="text-xs font-mono uppercase tracking-wider text-[#FFC107] mb-2">Llaves con chip</div>
            <h3 className="font-display font-bold text-3xl">Duplicado y programación in situ</h3>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-md border border-white/10 aspect-[4/3]">
          <img src={BMW_IMG} alt="BMW M5 F10 — servicio premium" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="text-xs font-mono uppercase tracking-wider text-[#FFC107] mb-2">Premium</div>
            <h3 className="font-display font-bold text-3xl">Especialistas en vehículos premium</h3>
            <p className="text-sm text-[#A1A1AA] mt-2">BMW, Mercedes, Audi, Porsche, Tesla y más.</p>
          </div>
        </div>
      </section>

      {/* COVERAGE ZONES */}
      <section id="cobertura" className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24">
          <div className="grid lg:grid-cols-3 gap-10 mb-10">
            <div className="lg:col-span-2">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FFC107] mb-3">03 · Zonas de cobertura</div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">
                Desde Tàrrega,<br />
                <span className="text-amber-grad">100 km a la redonda.</span>
              </h2>
            </div>
            <p className="text-sm text-[#A1A1AA] leading-relaxed self-end">
              Cubrimos toda la zona central de Catalunya y la franja oriental de Aragón. Si tu localidad no aparece y crees que entra en el radio, escríbenos por WhatsApp.
            </p>
          </div>

          <div className="sc-card rounded-md p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-md bg-[#FFC107] flex items-center justify-center">
                <MapPin className="w-4 h-4 text-black" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-semibold text-lg">Tàrrega (Lleida)</div>
                <div className="text-xs font-mono uppercase tracking-wider text-[#71717A]">Punto base · Radio 100 km</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-1.5 text-sm">
              {[
                "Lleida", "Cervera", "Mollerussa", "Balaguer",
                "Les Borges Blanques", "L'Espluga de Francolí", "Montblanc", "Valls",
                "Tarragona", "Reus", "Salou", "Cambrils",
                "Vila-seca", "El Vendrell", "Cunit", "Sitges",
                "Vilafranca del Penedès", "Igualada", "Calaf", "Manresa",
                "Solsona", "Vic", "Falset", "Mora d'Ebre",
                "Fraga", "Barbastro", "Huesca", "Monzón",
              ].map((city) => (
                <div key={city} className="flex items-center gap-2 py-1 group">
                  <span className="w-1 h-1 rounded-full bg-[#FFC107] group-hover:scale-150 transition-transform" />
                  <span className="text-white group-hover:text-[#FFC107] transition-colors">{city}</span>
                </div>
              ))}
            </div>

            <div className="mt-7 pt-6 border-t border-white/5 flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
              <p className="text-xs text-[#71717A] font-mono">Desplazamiento 1,50€/km solo ida. Cálculo automático por GPS.</p>
              <a
                href="https://wa.me/34641719027?text=Hola,%20quería%20consultar%20si%20mi%20zona%20entra%20en%20cobertura."
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2"
                data-testid="coverage-whatsapp-btn"
              >
                Consultar mi zona por WhatsApp <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
